import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Crown, Gem, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { useMe } from "@/context/MeContext";
import { STATUS, errMsg, eur, publicGet, useApi } from "@/lib/api";
import { Seo } from "@/components/Seo";

const RANK = { bronze: 0, gold: 1, diamond: 2 };
const EXTRA = { gold: 0.003, diamond: 0.005 };
const PERKS = {
  gold: ["Prezzi Gold su tutto il Marketplace Partner", "I tuoi punti valgono il 60% in più (1.000 pt = 8 €)", "Status attivo subito, valido fino al 31/12 dell'anno prossimo"],
  diamond: ["I prezzi più bassi del Marketplace: tariffa Diamond", "I tuoi punti valgono il doppio (1.000 pt = 10 €)", "Status attivo subito, valido fino al 31/12 dell'anno prossimo", "Il livello più alto del club"],
};

function PlanCard({ level, price, current, onBuy, year }) {
  const s = STATUS[level];
  const Icon = level === "gold" ? Crown : Gem;
  const owned = RANK[current] >= RANK[level];
  return (
    <div data-testid={`plan-${level}`} className={`shine relative flex flex-col rounded-3xl border bg-gradient-to-br p-8 ${s.card} ${s.ring}`} style={{ boxShadow: `0 30px 80px -30px ${s.color}88` }}>
      {level === "diamond" && <span className="absolute right-6 top-6 rounded-full bg-sky-400 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-black">Più conveniente</span>}
      <Icon className="h-8 w-8" style={{ color: s.color }} />
      <p className="mt-4 font-club text-2xl font-bold" style={{ color: s.color }}>{s.label.toUpperCase()}</p>
      <p className="mt-2 font-display text-5xl font-extrabold text-white" data-testid={`plan-price-${level}`}>{eur(price)}</p>
      <p className="text-sm text-slate-400">una tantum · valido fino al 31/12/{year + 1}</p>
      <ul className="mt-6 flex-1 space-y-3">
        {PERKS[level].map((p) => <li key={p} className="flex gap-2 text-sm text-slate-200"><Check className="mt-0.5 h-4 w-4 shrink-0" style={{ color: s.color }} /> {p}</li>)}
      </ul>
      <button data-testid={`buy-status-${level}`} disabled={owned} onClick={() => onBuy(level)}
        className="mt-8 h-12 rounded-xl font-semibold text-black transition-transform duration-200 hover:scale-[1.02] disabled:opacity-50" style={{ background: s.color }}>
        {owned ? "Hai già questo livello" : `Diventa ${s.label}`}
      </button>
    </div>
  );
}

function Calculator({ plans }) {
  const [mkt, setMkt] = useState(150);
  const [club, setClub] = useState(300);
  const row = (lvl) => {
    const pct = lvl === "gold" ? plans.avg_save_gold_pct : plans.avg_save_diamond_pct;
    const monthly = mkt * pct + (club / 1.22) * 2 * EXTRA[lvl];
    const price = plans.prices[lvl];
    return { yearly: monthly * 12, months: monthly > 0 ? Math.ceil(price / monthly) : null, net: monthly * 12 - price };
  };
  return (
    <div className="rounded-3xl border border-white/10 bg-[#12141C] p-6 sm:p-8" data-testid="roi-calculator">
      <h3 className="font-display text-2xl font-semibold text-white">Calcola quanto recuperi</h3>
      <div className="mt-6 grid gap-8 lg:grid-cols-2">
        <div className="space-y-6">
          <div>
            <p className="flex justify-between text-sm text-slate-300"><span>Spesa mensile nel Marketplace</span><b data-testid="calc-mkt-value" className="text-white">{eur(mkt)}</b></p>
            <Slider data-testid="calc-mkt-slider" className="mt-3" value={[mkt]} min={0} max={1000} step={10} onValueChange={(v) => setMkt(v[0])} />
          </div>
          <div>
            <p className="flex justify-between text-sm text-slate-300"><span>Acquisti mensili con "Fatelo voi"</span><b data-testid="calc-club-value" className="text-white">{eur(club)}</b></p>
            <Slider data-testid="calc-club-slider" className="mt-3" value={[club]} min={0} max={3000} step={50} onValueChange={(v) => setClub(v[0])} />
          </div>
          <p className="text-xs text-slate-500">Sconto medio reale delle offerte partner: Gold {(plans.avg_save_gold_pct * 100).toFixed(1)}%, Diamond {(plans.avg_save_diamond_pct * 100).toFixed(1)}% sul prezzo Bronze. Il calcolo include anche il maggior valore dei punti.</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {["gold", "diamond"].map((l) => {
            const r = row(l);
            return (
              <div key={l} data-testid={`calc-result-${l}`} className="rounded-2xl border p-5" style={{ borderColor: `${STATUS[l].color}55`, background: `${STATUS[l].color}10` }}>
                <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: STATUS[l].color }}>{STATUS[l].label}</p>
                <p className="mt-2 text-sm text-slate-400">Vantaggio in un anno</p>
                <p className="font-display text-3xl font-bold text-white">{eur(r.yearly)}</p>
                <p className="mt-2 text-sm text-slate-300">{r.months ? <>Recuperi i {eur(plans.prices[l])} in <b className="text-white">{r.months} {r.months === 1 ? "mese" : "mesi"}</b></> : "Aumenta la spesa per vedere il recupero"}</p>
                {r.net > 0 && <p className="mt-1 text-sm text-emerald-400">Guadagno netto {eur(r.net)}/anno</p>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Examples({ plans }) {
  const ex = plans.examples.filter((e) => e.save_gold > 0 || e.save_diamond > 0);
  if (!ex.length) return null;
  return (
    <div className="mt-16">
      <h2 className="font-display text-base font-semibold uppercase tracking-widest text-slate-400 md:text-lg">Bastano pochi acquisti</h2>
      <div className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {ex.map((e) => (
          <div key={e.id} data-testid={`example-${e.id}`} className="overflow-hidden rounded-3xl border border-white/10 bg-[#12141C]">
            {e.image_url && <img src={e.image_url} alt={e.name} className="aspect-[16/9] w-full object-cover" />}
            <div className="space-y-2 p-5">
              <p className="text-xs uppercase tracking-widest text-slate-500">{e.partner_name}</p>
              <p className="font-display text-lg font-semibold text-white">{e.name}</p>
              <p className="text-sm text-slate-400">Bronze <span className="line-through">{eur(e.price_bronze)}</span></p>
              {["gold", "diamond"].map((l) => {
                const save = l === "gold" ? e.save_gold : e.save_diamond;
                if (save <= 0) return null;
                const n = Math.ceil(plans.prices[l] / save);
                return (
                  <p key={l} className="text-sm text-slate-300">
                    <b style={{ color: STATUS[l].color }}>{STATUS[l].label} {eur(e[`price_${l}`])}</b> · risparmi {eur(save)} →{" "}
                    {n <= 1 ? <b className="text-emerald-400">ripagato al primo acquisto</b> : <>ripagato in <b className="text-white">{n} acquisti</b></>}
                  </p>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function StatusShop() {
  const { me, isSignedIn, refresh } = useMe();
  const api = useApi();
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [buying, setBuying] = useState(null);
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const year = new Date().getFullYear();

  useEffect(() => { publicGet("/public/status-plans").then(setPlans).catch(() => {}); }, []);
  useEffect(() => { if (me?.phone) setPhone(me.phone); }, [me]);

  const onBuy = (level) => (isSignedIn ? setBuying(level) : navigate("/sign-in"));
  const confirm = async () => {
    setBusy(true);
    try {
      const order = await api("post", "/status/purchase", { level: buying, phone });
      await refresh();
      toast.success("Richiesta inviata! Effettua il bonifico per attivare lo status.");
      navigate("/ordine-confermato", { state: { orders: [order] } });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!plans) return <p className="py-24 text-center text-slate-500">Caricamento…</p>;
  const current = me?.status || "bronze";
  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6" data-testid="status-shop-page">
      <Seo title="Diventa Gold o Diamond" description="Attiva subito lo status Gold (50 €) o Diamond (100 €): prezzi riservati sul Marketplace e punti che valgono di più. Si ripaga in pochi acquisti." />
      <div className="fade-up max-w-3xl">
        <p className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-amber-300"><Sparkles className="h-3.5 w-3.5" /> Status premium</p>
        <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">Paga meno da subito.<br /><span className="bg-gradient-to-r from-amber-200 via-amber-400 to-sky-300 bg-clip-text text-transparent">Lo status si ripaga da solo.</span></h1>
        <p className="mt-5 text-base text-slate-400 md:text-lg">Non aspettare di accumulare 1.000 o 5.000 punti: attiva Gold o Diamond oggi, ottieni i prezzi riservati sul Marketplace e fai valere di più ogni punto. Dopo pochi acquisti hai già recuperato la spesa.</p>
      </div>
      <div className="mt-12 grid gap-6 md:grid-cols-2">
        {["gold", "diamond"].map((l) => <PlanCard key={l} level={l} price={plans.prices[l]} current={current} onBuy={onBuy} year={year} />)}
      </div>
      <Examples plans={plans} />
      <div className="mt-16"><Calculator plans={plans} /></div>
      <div className="mt-16 grid gap-5 md:grid-cols-3 text-sm text-slate-400">
        <p><b className="block text-white">Attivazione rapida</b>Paghi con bonifico istantaneo: lo status si attiva appena confermiamo il pagamento.</p>
        <p><b className="block text-white">Nessun rinnovo automatico</b>Paghi una volta sola. Per l'anno successivo puoi mantenerlo facendo i punti o riacquistarlo.</p>
        <p><b className="block text-white">I punti non si perdono</b>Il saldo punti resta tuo e da subito vale di più quando lo usi.</p>
      </div>
      {buying && (
        <Dialog open onOpenChange={(v) => !v && setBuying(null)}>
          <DialogContent data-testid="buy-status-dialog" className="border-white/10 bg-[#12141C] sm:max-w-md">
            <DialogHeader><DialogTitle className="font-display text-xl text-white">Attiva lo status {STATUS[buying].label}</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-400">Importo <b className="text-white">{eur(plans.prices[buying])}</b> da pagare con bonifico istantaneo. Lo status sarà valido fino al 31/12/{year + 1}.</p>
            <div><Label className="text-xs text-slate-400">Cellulare (ricevi l'SMS di conferma)</Label><Input data-testid="buy-status-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+39 333 1234567" className="mt-1 bg-[#1C1F2C]" /></div>
            <button data-testid="buy-status-confirm" disabled={busy} onClick={confirm} className="flex h-12 items-center justify-center gap-2 rounded-xl bg-amber-500 font-semibold text-black hover:bg-amber-400 disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Conferma e ricevi i dati per il bonifico
            </button>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
